import React, { useState, useCallback } from 'react';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Wrench,
  AlertTriangle,
  Package,
  Wallet,
  CreditCard,
  Calendar,
  RefreshCw
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';

const EnhancedDashboard = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Fetch dashboard data
  const { data, isLoading, isError, refetch: refetchOverview } = useQuery(
    ['enhanced-dashboard', selectedBranch?.id, dateRange],
    async () => {
      const response = await api.get('/api/dashboard/overview', {
        params: {
          branchId: selectedBranch?.id,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
    }
  );

  // Fetch history data for charts (dynamic based on dateRange)
  const { data: historyData, refetch: refetchHistory } = useQuery(
    ['dashboard-history', selectedBranch?.id, dateRange],
    async () => {
      const response = await api.get('/api/dashboard/history', {
        params: {
          branchId: selectedBranch?.id,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
    }
  );

  // Fetch company settings
  const { data: companySettings } = useQuery(
    'company-settings',
    async () => {
      const response = await api.get('/api/settings/company');
      return response.data.data;
    }
  );

  const handleRefresh = useCallback(() => {
    refetchOverview();
    refetchHistory();
  }, [refetchOverview, refetchHistory]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
        <span className="ml-3 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-600 font-medium">Failed to load dashboard data.</p>
        <button
          onClick={handleRefresh}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  const primaryMetrics = data?.primaryMetrics || {};
  const secondaryMetrics = data?.secondaryMetrics || {};
  const topSaleItems = data?.topSaleItems || [];
  const stockWarnings = data?.stockWarnings || [];

  // Prepare chart data — revenue, expense, and computed profit
  const prepareChartData = () => {
    if (!historyData) return [];
    
    const { monthlyRevenue = [] } = historyData;
    
    return monthlyRevenue.map(item => ({
      month: formatMonthName(item.month),
      revenue: Number(item.revenue) || 0,
      expense: Number(item.expenses) || 0,
      profit: Number(item.profit) || 0
    }));
  };

  const formatMonthName = (monthStr) => {
    const parts = monthStr.split('-');
    if (parts.length === 3) {
      // YYYY-MM-DD format
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // YYYY-MM format
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
  };

  const chartData = prepareChartData();

  return (
    <div className="space-y-6 bg-[#FFF8DC]/60 p-4 sm:p-6 rounded-2xl border border-amber-200/40 shadow-sm">
      {/* Responsive Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white/80 backdrop-blur p-4 rounded-xl border border-amber-200/30 shadow-sm">
        <div className="flex items-center gap-4">
          {companySettings?.logo ? (
            <img
              src={companySettings.logo.startsWith('http') ? companySettings.logo : `${api.defaults.baseURL || ''}${companySettings.logo}`}
              alt="Company Logo"
              className="w-14 h-14 rounded-xl object-contain bg-slate-50 p-1 border shadow-inner"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-14 h-14 bg-gradient-to-br from-amber-50 to-amber-100 text-amber-600 rounded-xl flex items-center justify-center font-bold text-xl border border-amber-200">
              🏢
            </div>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {companySettings?.companyName || 'Dashboard Snapshot'}
            </h1>
            <p className="text-sm text-gray-600 mt-0.5">
              Welcome back! Branch: <span className="font-semibold text-gray-800">{selectedBranch?.name || 'your store'}</span>
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 shadow-sm text-sm transition-colors active:bg-gray-100"
            title="Refresh dashboard"
          >
            <RefreshCw className="w-4 h-4 animate-spin-hover" />
            <span>Refresh</span>
          </button>
          
          {/* Date Range Selector */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-white p-2 border border-gray-300 rounded-lg shadow-sm">
            <div className="flex items-center justify-between gap-2 sm:justify-start">
              <span className="text-[10px] sm:text-xs text-gray-550 font-bold uppercase whitespace-nowrap">From:</span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none bg-gray-50 flex-1 sm:flex-none"
              />
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-start">
              <span className="text-[10px] sm:text-xs text-gray-550 font-bold uppercase whitespace-nowrap">To:</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none bg-gray-50 flex-1 sm:flex-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Primary Metrics - 5 Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* PURCHASE Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">PRODUCT PURCHASE</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{primaryMetrics.purchase?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <ShoppingCart className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* EXPENSES Card */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">EXPENSES</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{secondaryMetrics.expenses?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <TrendingDown className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* SALE Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">PRODUCT SALE</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{primaryMetrics.sale?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <DollarSign className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* JOB VALUE Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">CHARGES</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{primaryMetrics.jobValue?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <Wrench className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* PROFIT Card */}
        <div className={`bg-gradient-to-br ${primaryMetrics.profit >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-red-500 to-red-600'} rounded-xl shadow-lg p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90">PROFIT</p>
              <p className="text-3xl font-bold mt-2">{currencySymbol}{primaryMetrics.profit?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              {primaryMetrics.profit >= 0 ? (
                <TrendingUp className="w-8 h-8" />
              ) : (
                <TrendingDown className="w-8 h-8" />
              )}
            </div>
          </div>
        </div>
      </div>
              <p className="text-[10px] flex justify-center italic"> (product purchase + expenses) - (product sale + charges) = profit</p>

      {/* Secondary Metrics - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ALL JOBS Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">ALL JOBS</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">{secondaryMetrics.allJobs || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Total jobs in period</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-lg">
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
          </div>
        </div>

        {/* PENDING JOBS Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">PENDING JOBS</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">{secondaryMetrics.pendingJobs || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting action</p>
            </div>
            <div className="bg-red-100 p-4 rounded-lg">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Trend Chart */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profit Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis 
                dataKey="month" 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${currencySymbol}${value.toLocaleString()}`}
              />
              <Tooltip 
                formatter={(value) => [
                  `${currencySymbol}${Number(value).toLocaleString()}`,
                  'Profit'
                ]}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="profit" 
                fill="#10b981" 
                name="Profit"
                radius={[4, 4, 0, 0]}
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Last Six Month Expense Chart */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Last Six Month Expense</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${currencySymbol}${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`${currencySymbol}${Number(value).toLocaleString()}`, 'Expense']}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="expense" 
                stroke="#ef4444" 
                strokeWidth={3}
                dot={{ fill: '#ef4444', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 Sale Item Table */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Sale Item</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topSaleItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{item.itemName}</div>
                      <div className="text-xs text-gray-500">{item.brand} {item.model}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{Math.abs(item.totalQuantity)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      {currencySymbol}{Number(item.totalAmount).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {topSaleItems.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                      No sales data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stock Warning Table */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Stock Warning</h3>
            <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">
              {stockWarnings.length} Items
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stockWarnings.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">
                        {item.category?.name} - {item.brand} {item.model}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-red-600">
                        {item.currentStock} / {item.minStockLimit}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      {currencySymbol}{item.closingValue?.toLocaleString()}
                    </td>
                  </tr>
                ))}
                {stockWarnings.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      No stock warnings
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <p className="text-xs text-gray-600">BANK</p>
          <p className="text-2xl font-bold text-gray-900">{currencySymbol}{secondaryMetrics.bank?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <p className="text-xs text-gray-600">CASH</p>
          <p className="text-2xl font-bold text-gray-900">{currencySymbol}{secondaryMetrics.cash?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <p className="text-xs text-gray-600">INVENTORY VALUE</p>
          <p className="text-2xl font-bold text-gray-900">{currencySymbol}{secondaryMetrics.inventoryValue?.toLocaleString() || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;
